import ApplicationController from 'controllers/application-controller'
import { useIntersection } from 'stimulus-use'
import MediaQuery from 'lib/media-query'
import Lazy from 'frontend/lib/glide/lazy'
import Autoplay from 'frontend/lib/glide/autoplay'
import Glide, { Swipe, Images, Controls, Breakpoints } from '@glidejs/glide/dist/glide.modular.esm'

export default class GlideSliderController extends ApplicationController {
  static values = { settings: Object }
  static targets = ['track']
  static outlets = ['control']
  static plugins = { Images, Swipe, Lazy, Breakpoints, Autoplay, Controls }

  static get defaultSettings () {
    return {
      type: 'slider',
      lazyGlide: {
        selector: '.lazy',
        wokenSlide: 'slider__slide--woken',
      },
      focusAt: 'center',
      startAt: 2,
      gap: 0,
      perView: 4,
      hoverpause: true,
      autoplay: 4000,
      animationDuration: 400,
      animationTimingFunc: 'linear',
      classes: {
        direction: {
          ltr: 'slider--ltr',
          rtl: 'slider--rtl',
        },
        slider: 'slider--slider',
        carousel: 'slider--carousel',
        swipeable: 'slider--swipeable',
        dragging: 'slider--dragging',
        cloneSlide: 'slider__slide--clone',
        activeNav: 'slider__bullet--active',
        activeSlide: 'slider__slide--active',
        disabledArrow: 'slider__arrow--disabled',
      },
    }
  }

  connect () {
    super.connect()
    this.slider = null
    this.mounted = false
    this.mounting = false
    this.onScreen = false
    useIntersection(this)
    this.processSettings()
    if (this.element.id) window.addEventListener('hashchange', () => this.#handleHashChange())
    this.#handleHashChange()
  }

  // Leave only glide settings
  processSettings () {
    this.mountOnScreens = this.settingsValue.mountOnScreens
    delete this.settingsValue.mountOnScreens
  }

  #handleHashChange () {
    const hash = window.location.hash
    if (hash.startsWith(`#${this.element.id}=`)) {
      const indexValue = parseInt(hash.split('=')[1])
      if (!isNaN(indexValue)) {
        this.#setSlideIndex(indexValue)
      } else {
        console.error(`Invalid index format in URL hash: ${hash}`)
      }
    }
  }

  #setSlideIndex (index) {
    if (this.slider && this.mounted) {
      console.debug('navigating to', index)
      this.slider.go(`=${index}`)
    } else {
      console.debug('setting startAt', index)
      this.glideSettings.startAt = index
    }
  }

  // Screen apppearance
  appear () {
    console.debug('appear()')
    this.onScreen = true
    if (this.mountOnScreens) {
      MediaQuery.addListener('change', () => this.mediaChange)
    } else {
      this.#mountSlider()
    }
  }

  // Screen apppearance
  disappear () {
    // console.debug('disappear()')
    // this.onScreen = false
    // MediaQuery.removeListener('change', () => this.mediaChange)
    // this.#destroySlider()
  }

  // Screen size change
  mediaChange (current) {
    const { smallerThan } = this.mountOnScreens

    smallerThan && (current.unfits(smallerThan) ? this.#mountSlider() : this.#destroySlider())
  }

  #mountSlider () {
    if (this.mounting || this.mounted) {
      console.debug('Slider is already mounting or mounted')
      return
    }
    console.debug('Mounting slider')
    this.mounting = true
    this.slider = new Glide(this.element, this.glideSettings)
    this.slider.on('mount.after', () => {
      console.debug('Slider mounted')
      this.mounted = true
    })

    this.slider.mount({ Images, Swipe, Lazy, Breakpoints, Autoplay, Controls })
    this.mounting = false
  }

  #destroySlider () {
    if (this.slider && this.mounted) {
      this.slider.destroy()
      this.slider = null
      this.mounted = false
      console.debug('Slider destroyed')
    } else {
      console.debug('Slider is not mounted')
    }
  }

  get glideSettings () {
    if (this._glideSettings) return this._glideSettings
    this._glideSettings = Object.assign(Object.create(this.constructor.defaultSettings), this.settingsValue)
    return this._glideSettings
  }
}
